import React, { useState } from 'react';
import { BloodPressureRecord, DailySummary } from '@/types/record';
import { formatDate, getTodayDateString } from '@/utils/dateUtils';
import { StampBadge } from '@/components/common/StampBadge';
import { RecordItem } from './RecordItem';
import { DailySummaryCard } from './DailySummaryCard';

export interface DailyRecordGroupProps {
  date: string;
  records: BloodPressureRecord[];
  summary: DailySummary;
  formattedDateZh?: string;
  onEditRecord?: (record: BloodPressureRecord) => void;
  onDeleteRecord?: (id: number) => void;
  onAddRecordForDate?: (date: string) => void;
  defaultExpanded?: boolean;
  className?: string;
}

export const DailyRecordGroup: React.FC<DailyRecordGroupProps> = ({
  date,
  records,
  summary,
  formattedDateZh,
  onEditRecord,
  onDeleteRecord,
  onAddRecordForDate,
  defaultExpanded = true,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  const displayDateZh = formattedDateZh || formatDate(date, 'fullZh');
  const isToday = date === getTodayDateString();

  return (
    <div
      className={`
        bg-paper-100 border border-paper-400 rounded-xs shadow-paper overflow-hidden transition-all duration-150
        ${className}
      `}
    >
      {/* Group Header (Clickable Accordion Bar) */}
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="px-4 sm:px-6 py-3.5 bg-paper-200/90 hover:bg-paper-300/80 cursor-pointer border-b border-paper-400 select-none transition-colors"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Date & Tags */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-serif font-black text-sm sm:text-base text-ink-900 tracking-wider uppercase">
              {displayDateZh}
            </span>

            {isToday && (
              <span className="font-serif text-[11px] bg-red-100 text-red-900 border border-red-300 px-2 py-0.2 rounded-xs font-bold">
                今日
              </span>
            )}

            <span className="font-serif text-[11px] bg-paper-100 text-ink-600 border border-paper-300 px-2 py-0.2 rounded-xs">
              {records.length} 次量測
            </span>
          </div>

          {/* Right: Daily Average Quick Indicators & Expand Toggle */}
          <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
            <div className="flex items-center gap-2 font-mono text-xs text-ink-800 bg-white/90 px-2.5 py-1 rounded-xs border border-paper-300">
              <span>日均: <strong>{summary.avg_systolic}</strong> / <strong>{summary.avg_diastolic}</strong></span>
              <span className="text-paper-400">|</span>
              <span>脈搏: <strong>{summary.avg_pulse}</strong></span>
            </div>

            {summary.category && (
              <StampBadge category={summary.category} size="sm" />
            )}

            <div className="w-6 h-6 flex items-center justify-center text-ink-600 text-sm font-mono transition-transform duration-200">
              {isExpanded ? '▲' : '▼'}
            </div>
          </div>
        </div>
      </div>

      {/* Group Content Body */}
      {isExpanded && (
        <div className="p-4 sm:p-6 space-y-4 bg-white/60">
          {/* Highlighted Daily Summary Card */}
          {records.length > 0 && (
            <DailySummaryCard summary={summary} variant="full" />
          )}

          {/* Measurements List Header with Quick Add Button */}
          <div className="flex items-center justify-between pt-1 border-t border-paper-300">
            <h4 className="font-serif text-xs font-bold uppercase tracking-wider text-ink-700">
              當日量測紀錄明細 ({records.length} 筆)
            </h4>

            {onAddRecordForDate && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddRecordForDate(date);
                }}
                className="inline-flex items-center gap-1 font-serif text-xs text-ink-800 hover:text-ink-950 font-semibold px-2.5 py-1 bg-paper-100 hover:bg-paper-200 border border-paper-400 rounded-xs transition-colors"
              >
                <span>+ 登記此日數據</span>
              </button>
            )}
          </div>

          {/* Chronological Record Items */}
          <div className="space-y-2.5">
            {records.map((record) => (
              <RecordItem
                key={record.id}
                record={record}
                onEdit={onEditRecord}
                onDelete={onDeleteRecord}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
